import { Button } from "@workspace/ui/components/button";
import { Calendar } from "@workspace/ui/components/calendar";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import { Separator } from "@workspace/ui/components/separator";
import { cn } from "@workspace/ui/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { HTMLAttributes } from "react";
import { useEffect, useRef, useState } from "react";
import type { DateRange } from "react-day-picker";
import { useDebounceCallback } from "usehooks-ts";
import type { DatePreset } from "@/components/data-table/types";
import { kbdVariants } from "@/components/kbd-variants";
import { presets as defaultPresets } from "@/config/constants";

interface DatePickerWithRangeProps extends HTMLAttributes<HTMLDivElement> {
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  presets?: DatePreset[];
}

export function DatePickerWithRange({
  className,
  date,
  setDate,
  presets = defaultPresets,
}: DatePickerWithRangeProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (!open) {
        return;
      }

      presets.forEach((preset) => {
        if (preset.shortcut === e.key) {
          setDate({ from: preset.from, to: preset.to });
        }
      });
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [setDate, presets, open]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "max-w-full justify-start truncate text-left font-normal hover:bg-muted/50",
              !date && "text-muted-foreground"
            )}
            id="date"
            size="sm"
            variant="outline"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <span className="truncate">
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </span>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <div className="flex flex-col justify-between sm:flex-row">
            <div className="hidden sm:block">
              <DatePresets
                onSelect={setDate}
                presets={presets}
                selected={date}
              />
            </div>
            <div className="block p-3 sm:hidden">
              <DatePresetsSelect
                onSelect={setDate}
                presets={presets}
                selected={date}
              />
            </div>
            <Separator className="h-auto w-px" orientation="vertical" />
            <Calendar
              defaultMonth={date?.from}
              mode="range"
              numberOfMonths={1}
              onSelect={setDate}
              selected={date}
            />
          </div>
          <Separator />
          <CustomDateRange onSelect={setDate} selected={date} />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function DatePresets({
  selected,
  onSelect,
  presets,
}: {
  selected: DateRange | undefined;
  onSelect: (date: DateRange | undefined) => void;
  presets: DatePreset[];
}) {
  return (
    <div className="flex flex-col gap-2 p-3">
      <p className="mx-3 text-muted-foreground text-xs uppercase">Date Range</p>
      <div className="grid gap-1">
        {presets.map(({ label, shortcut, from, to }) => {
          const isActive = selected?.from === from && selected?.to === to;
          return (
            <Button
              className={cn(
                "flex items-center justify-between gap-6",
                !isActive && "border border-transparent"
              )}
              key={label}
              onClick={() => onSelect({ from, to })}
              size="sm"
              variant={isActive ? "outline" : "ghost"}
            >
              <span className="mr-auto">{label}</span>
              <span className={cn(kbdVariants(), "uppercase")}>{shortcut}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

function DatePresetsSelect({
  selected,
  onSelect,
  presets,
}: {
  selected: DateRange | undefined;
  onSelect: (date: DateRange | undefined) => void;
  presets: DatePreset[];
}) {
  function findPreset(from?: Date, to?: Date) {
    return presets.find((p) => p.from === from && p.to === to)?.shortcut;
  }

  const value = findPreset(selected?.from, selected?.to);

  return (
    <Select
      onValueChange={(v) => {
        const preset = presets.find((p) => p.shortcut === v);
        if (preset) {
          onSelect({ from: preset.from, to: preset.to });
        }
      }}
      value={value}
    >
      <SelectTrigger>
        <SelectValue placeholder="Date Presets" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Date Presets</SelectLabel>
          {presets.map(({ label, shortcut }) => (
            <SelectItem
              className="flex items-center justify-between [&>span:last-child]:flex [&>span:last-child]:w-full [&>span:last-child]:justify-between"
              key={label}
              value={shortcut}
            >
              <span>{label}</span>
              <span
                className={cn(kbdVariants(), "ml-2 h-5 uppercase leading-snug")}
              >
                {shortcut}
              </span>
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

// REMINDER: We can add min max date range validation https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#setting_maximum_and_minimum_dates_and_times
function formatDateForInput(date: Date | undefined): string {
  if (!date) {
    return "";
  }
  const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return utcDate.toISOString().slice(0, 16);
}

function CustomDateRange({
  selected,
  onSelect,
}: {
  selected: DateRange | undefined;
  onSelect: (date: DateRange | undefined) => void;
}) {
  const dateFromRef = useRef<Date | undefined>(selected?.from);
  const dateToRef = useRef<Date | undefined>(selected?.to);
  // react-doctor-disable-next-line react-doctor/rerender-state-only-in-handlers -- isUserInput IS read in render (line 231 gates the prop-sync block); rule's transitive walk missed it
  const [isUserInput, setIsUserInput] = useState(false);
  const prevSelectedRef = useRef(selected);
  if (selected !== prevSelectedRef.current) {
    prevSelectedRef.current = selected;
    if (!isUserInput) {
      dateFromRef.current = selected?.from;
      dateToRef.current = selected?.to;
    }
  }

  // Debounce the commit (not the values) so editing settles before we notify
  // the parent. Resetting isUserInput here, in the same callback that commits,
  // lets the render-time sync above resume picking up `selected` afterwards.
  const commitRange = useDebounceCallback((range: DateRange) => {
    onSelect(range);
    setIsUserInput(false);
  }, 1000);

  return (
    <div className="flex flex-col gap-2 p-3">
      <p className="text-muted-foreground text-xs uppercase">Custom Range</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="grid w-full gap-1.5">
          <Label htmlFor="from">Start</Label>
          <Input
            defaultValue={formatDateForInput(selected?.from)}
            disabled={!selected?.from}
            id="from"
            key={`from-${formatDateForInput(selected?.from)}`}
            name="from"
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              if (!Number.isNaN(newDate.getTime())) {
                setIsUserInput(true);
                dateFromRef.current = newDate;
                commitRange({ from: newDate, to: dateToRef.current });
              }
            }}
            type="datetime-local"
          />
        </div>
        <div className="grid w-full gap-1.5">
          <Label htmlFor="to">End</Label>
          <Input
            defaultValue={formatDateForInput(selected?.to)}
            disabled={!selected?.to}
            id="to"
            key={`to-${formatDateForInput(selected?.to)}`}
            name="to"
            onChange={(e) => {
              const newDate = new Date(e.target.value);
              if (!Number.isNaN(newDate.getTime())) {
                setIsUserInput(true);
                dateToRef.current = newDate;
                commitRange({ from: dateFromRef.current, to: newDate });
              }
            }}
            type="datetime-local"
          />
        </div>
      </div>
    </div>
  );
}
